'use client';

import { useEffect, useMemo, useState, type FC } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { Check, CircleAlert, Copy, RefreshCwIcon } from 'lucide-react';
import { ActionBarPrimitive, useAuiState } from '@assistant-ui/react';

export type McpChatError = {
  code?: string;
  message: string;
  /** Tracking code cited in `message`, for support. */
  reference?: string;
};

/** Replies saved before `reference` existed only carry the run id in the text. */
const UUID_PATTERN =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

export function readChatError(value: unknown): McpChatError | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  const message = typeof row.message === 'string' ? row.message.trim() : '';
  if (!message) return null;
  const code = typeof row.code === 'string' ? row.code.trim() : '';
  const reference =
    (typeof row.reference === 'string' ? row.reference.trim() : '') ||
    (code ? '' : (message.match(UUID_PATTERN)?.[0] ?? ''));
  return {
    message,
    ...(code ? { code } : {}),
    ...(reference ? { reference } : {})
  };
}

const COPIED_MS = 1600;

const CopyReference: FC<{ value: string }> = ({ value }) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = window.setTimeout(() => setCopied(false), COPIED_MS);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const Icon = copied ? Check : Copy;
  return (
    <button
      type="button"
      onClick={() => void copy()}
      dir="ltr"
      title={copied ? 'کپی شد' : 'کپی کد پیگیری'}
      aria-label={copied ? 'کپی شد' : `کپی کد پیگیری ${value}`}
      className="d-inline-flex align-items-center gap-1 mx-1 px-1 border-0 rounded bg-danger-subtle text-danger-emphasis font-monospace text-break align-baseline"
      style={{ cursor: 'pointer' }}
    >
      <span className="fw-bold">{value}</span>
      <Icon size={12} strokeWidth={2} aria-hidden className="flex-shrink-0" />
    </button>
  );
};

/** The message text, with the tracking code as a click-to-copy chip. */
const ErrorText: FC<{ error: McpChatError }> = ({ error }) => {
  const { message, reference } = error;
  if (!reference) return <>{message}</>;
  const at = message.indexOf(reference);
  if (at < 0) {
    return (
      <>
        {message} <CopyReference value={reference} />
      </>
    );
  }
  return (
    <>
      {message.slice(0, at)}
      <CopyReference value={reference} />
      {message.slice(at + reference.length)}
    </>
  );
};

/** Danger alert under the assistant reply when its turn failed. */
export const McpChatErrorAlert: FC = () => {
  // useAuiState uses Object.is — never return a fresh object from the selector.
  const errorKey = useAuiState((s) => {
    if (s.message.role !== 'assistant') return '';
    const raw = s.message.metadata?.custom?.chatError;
    if (!raw || typeof raw !== 'object') return '';
    try {
      return JSON.stringify(raw);
    } catch {
      return '';
    }
  });

  const error = useMemo(
    () => (errorKey ? readChatError(JSON.parse(errorKey)) : null),
    [errorKey]
  );

  if (!error) return null;

  return (
    <div className="mt-3">
      <Alert
        variant="danger"
        role="alert"
        className="mb-0 py-2 px-3 fs--1 d-flex align-items-start gap-2"
      >
        <CircleAlert
          className="flex-shrink-0 mt-1"
          size={16}
          strokeWidth={2}
          aria-hidden
        />
        <div className="flex-grow-1">
          <div>
            <ErrorText error={error} />
          </div>
          <div className="mt-2 d-flex justify-content-end">
            <ActionBarPrimitive.Reload asChild>
              <Button
                variant="outline-danger"
                size="sm"
                className="d-inline-flex align-items-center gap-1 py-0 px-2 fs--1"
              >
                <RefreshCwIcon size={12} strokeWidth={2} aria-hidden />
                تلاش مجدد
              </Button>
            </ActionBarPrimitive.Reload>
          </div>
        </div>
      </Alert>
    </div>
  );
};
